/**
 * Scenario System (Sprint 2b) - periodically assigns a volatility scenario
 * (historic chart-shape replay) to each meme coin. The active segment's
 * drift/volatilityMul feed into exchangeMarketSystem via scenarioBias /
 * scenarioVolMul. Majors never get scenarios (no entries in risk.scenarios).
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import type { CoinConfig } from '../config/coins.js';
import {
  SCENARIOS, SCENARIO_CONFIG, getScenario, scenarioDuration, segmentAt,
} from '../config/scenarios.js';
import type { ScenarioAssignState } from '../riskState.js';

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/**
 * Momentum bias contributed by the active scenario segment.
 * Balance guard: at/near the price floor the drift is ignored and a
 * recovery push is applied instead.
 */
export function scenarioBias(
  scn: ScenarioAssignState | undefined,
  cfg: CoinConfig,
  price: number,
  now: number
): number {
  if (!scn?.scenarioId) return 0;
  const scenario = getScenario(scn.scenarioId);
  if (!scenario) return 0;
  const seg = segmentAt(scenario, now - scn.startedAt);
  if (!seg) return 0;
  if (price <= cfg.minPrice * SCENARIO_CONFIG.guardPriceRatio) {
    return SCENARIO_CONFIG.recoveryDrift * cfg.volatility;
  }
  return seg.drift * cfg.volatility;
}

/** Noise multiplier of the active scenario segment (1 when idle). */
export function scenarioVolMul(scn: ScenarioAssignState | undefined, now: number): number {
  if (!scn?.scenarioId) return 1;
  const scenario = getScenario(scn.scenarioId);
  if (!scenario) return 1;
  return segmentAt(scenario, now - scn.startedAt)?.volatilityMul ?? 1;
}

/** End a finished scenario: schedule the rest period, mark pump ends. */
function endScenario(scn: ScenarioAssignState, scenarioId: string, now: number): ScenarioAssignState {
  const scenario = getScenario(scenarioId);
  return {
    ...scn,
    scenarioId: null,
    nextAt: now + randRange(SCENARIO_CONFIG.restMin, SCENARIO_CONFIG.restMax),
    pumpEndedAt: scenario?.endsWithPump ? now : scn.pumpEndedAt,
  };
}

function assignScenario(scn: ScenarioAssignState, coinId: string, now: number): ScenarioAssignState {
  const scenario = SCENARIOS[Math.floor(Math.random() * SCENARIOS.length)];
  console.debug('[scenario] assigned', coinId, scenario.id,
    `${Math.round(scenarioDuration(scenario) / 1000)}s`);
  return { ...scn, scenarioId: scenario.id, startedAt: now };
}

export const scenarioSystem: TickSystem<GameState, GameAction> = {
  name: 'scenario',

  update(state) {
    if (!state.isRunning || !state.risk || !state.exchange) return state;
    const now = Date.now();

    let changed = false;
    const scenarios: Record<string, ScenarioAssignState> = { ...state.risk.scenarios };
    for (const [coinId, scn] of Object.entries(state.risk.scenarios)) {
      // Only listed coins run scenarios; delisted / pool coins stay idle.
      if (state.exchange.listing[coinId]?.status !== 'listed') continue;
      if (scn.scenarioId) {
        const scenario = getScenario(scn.scenarioId);
        if (!scenario || now >= scn.startedAt + scenarioDuration(scenario)) {
          scenarios[coinId] = endScenario(scn, scn.scenarioId, now);
          changed = true;
        }
      } else if (now >= scn.nextAt) {
        scenarios[coinId] = assignScenario(scn, coinId, now);
        changed = true;
      }
    }

    return changed ? { ...state, risk: { ...state.risk, scenarios } } : state;
  },
};
